import { useState } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import type { SessionUser } from "@resume-cloudflare/shared";
import {
  Briefcase,
  ExternalLink,
  FileText,
  ListChecks,
  Plus,
  Sparkles,
} from "lucide-react";
import { useCronHealth, type CronHealth } from "../lib/runs";
import { useJobs, useShortlist, type JobRow, type ShortlistResponse } from "../lib/jobs";
import {
  APPLICATION_STATUSES,
  useApplications,
  useCreateApplication,
  type ApplicationStatus,
} from "../lib/applications";
import { useResumes } from "../lib/resumes";
import { Button } from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { Pill, type PillTone } from "../components/ui/Pill";
import { ScorePill } from "../components/jobs/ScorePill";
import { Skeleton } from "../components/ui/Skeleton";
import { Dialog } from "../components/ui/Dialog";
import { Input } from "../components/ui/Input";
import { Select } from "../components/ui/Select";
import { RecommendTile } from "../components/home/RecommendTile";
import { ReviewQueueTile } from "../components/home/ReviewQueueTile";
import { TodayTile } from "../components/home/TodayTile";
import { StuckApplicationsTile } from "../components/home/StuckApplicationsTile";
import { WeeklySummaryTile } from "../components/home/WeeklySummaryTile";
import { PostingAlertsTile } from "../components/home/PostingAlertsTile";
import { LeadsTile } from "../components/home/LeadsTile";

export default function Home({ user }: { user: SessionUser }) {
  return (
    <section className="mx-auto max-w-6xl space-y-6 p-4 sm:p-6 lg:p-8">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Welcome, {user.name ?? user.email}
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          A quick read on the pipeline plus today&rsquo;s highest-fit roles.
        </p>
      </header>

      <CronStrip />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[16rem_1fr]">
        <QuickActionsSidebar />
        <main className="space-y-6">
          <ReviewQueueTile />
          <CountsGrid />
          <PostingAlertsTile />
          <LeadsTile />
          <TodayTile />
          <WeeklySummaryTile />
          <RecommendTile />
          <StuckApplicationsTile />
          <TodaysShortlist />
        </main>
      </div>
    </section>
  );
}

function CronStrip() {
  const cron = useCronHealth();
  if (cron.isLoading) {
    return (
      <Card>
        <CardBody className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-3 w-44" />
          <Skeleton className="h-3 w-40" />
        </CardBody>
      </Card>
    );
  }
  if (cron.error || !cron.data) {
    return (
      <Card>
        <CardBody className="text-xs text-danger-fg">
          Couldn&rsquo;t load cron health.
        </CardBody>
      </Card>
    );
  }

  const data = cron.data;
  const last = data.last_run;
  const ageMs = last ? Date.now() - last.started_at : null;
  const expectedNextAt = last
    ? last.started_at - (data.last_skew_ms ?? 0) + data.expected_interval_ms
    : null;
  const overdue =
    expectedNextAt !== null && Date.now() - expectedNextAt > 30 * 60 * 1000;

  return (
    <Card>
      <CardBody className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-neutral-400">
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500">
            Last cron tick
          </span>
          <span className="font-medium text-neutral-100">
            {last && ageMs !== null ? `${formatAge(ageMs)} ago` : "—"}
          </span>
          {last ? <CronStatusPill data={data} /> : null}
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-wider text-neutral-500">
            Next expected
          </span>
          <span className="text-neutral-200">
            {expectedNextAt
              ? new Date(expectedNextAt).toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                  timeZoneName: "short",
                })
              : "—"}
          </span>
          {overdue ? <Pill tone="danger">overdue</Pill> : null}
        </div>
        <div className="ml-auto">
          <Link
            to="/runs"
            className="text-neutral-300 underline-offset-4 hover:underline"
          >
            View runs →
          </Link>
        </div>
      </CardBody>
    </Card>
  );
}

function CronStatusPill({ data }: { data: CronHealth }) {
  const last = data.last_run;
  if (!last) return null;
  const tone: PillTone =
    last.status === "error" || last.status === "failed"
      ? "danger"
      : last.error_count > 0
        ? "warning"
        : "success";
  return <Pill tone={tone}>{last.status}</Pill>;
}

function CountsGrid() {
  const jobs = useJobs({ limit: 1 });
  const apps = useApplications();
  const resumes = useResumes();

  const appCounts = (() => {
    const acc: Record<ApplicationStatus, number> = {
      interested: 0,
      preparing: 0,
      approved: 0,
      applied: 0,
      interviewing: 0,
      offer: 0,
      rejected: 0,
      withdrawn: 0,
    };
    for (const a of apps.data?.applications ?? []) acc[a.status] += 1;
    return acc;
  })();
  const totalApps = apps.data?.applications.length ?? 0;

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <CountCard
        to="/jobs"
        title="Active jobs"
        loading={jobs.isLoading}
        error={jobs.error}
        primary={jobs.data?.total_active ?? 0}
        sub="across the catalog"
      />
      <CountCard
        to="/applications"
        title="Applications"
        loading={apps.isLoading}
        error={apps.error}
        primary={totalApps}
        sub={`${appCounts.applied} applied · ${appCounts.interviewing} interviewing`}
      />
      <CountCard
        to="/resumes"
        title="Resumes"
        loading={resumes.isLoading}
        error={resumes.error}
        primary={resumes.data?.resumes.length ?? 0}
        sub="variants on file"
      />
    </div>
  );
}

function CountCard({
  to,
  title,
  loading,
  error,
  primary,
  sub,
}: {
  to: string;
  title: string;
  loading: boolean;
  error: unknown;
  primary: number;
  sub: string;
}) {
  return (
    <Link
      to={to}
      className="group block rounded-lg border border-neutral-800 bg-neutral-900/40 p-4 shadow-sm transition-colors hover:border-neutral-700 hover:bg-neutral-900/60 focus-visible:border-accent-500"
    >
      <p className="text-[10px] uppercase tracking-wider text-neutral-500">
        {title}
      </p>
      {loading ? (
        <Skeleton className="mt-3 h-7 w-16" />
      ) : error ? (
        <p className="mt-2 text-xs text-danger-fg">failed</p>
      ) : (
        <p className="mt-1 text-3xl font-semibold tabular-nums text-neutral-100">
          {primary.toLocaleString()}
        </p>
      )}
      <p className="mt-1 text-xs text-neutral-500">{sub}</p>
    </Link>
  );
}

function TodaysShortlist() {
  const shortlist = useShortlist(10);

  return (
    <Card>
      <CardHeader
        title="Today's shortlist"
        description="Top 10 highest-fit roles scored in the last 30 days, excluding anything you're already tracking."
      />
      <CardBody>
        {shortlist.isLoading ? (
          <ShortlistSkeleton />
        ) : shortlist.error ? (
          <EmptyState
            tone="danger"
            title="Couldn't load shortlist"
            description="Try refreshing in a moment."
          />
        ) : !shortlist.data || shortlist.data.returned === 0 ? (
          <EmptyState
            icon={<Sparkles size={16} aria-hidden />}
            title="No shortlist yet"
            description="Once a few jobs are scored against your primary resume they'll show up here."
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  window.location.href = "/jobs";
                }}
              >
                Browse all jobs
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-neutral-800">
            {shortlist.data.jobs.map((job) => (
              <ShortlistRow key={`${job.company_id}:${job.job_id}`} job={job} />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function ShortlistSkeleton() {
  return (
    <ul className="divide-y divide-neutral-800">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-2/3" />
          </div>
          <Skeleton className="h-6 w-12" />
        </li>
      ))}
    </ul>
  );
}

function ShortlistRow({ job }: { job: JobRow }) {
  const qc = useQueryClient();
  const create = useCreateApplication();

  function trackInterested() {
    create.mutate(
      {
        url: job.url,
        role: job.title,
        company_id: job.company_id,
        status: "interested",
      },
      {
        onSuccess: (data) => {
          if (data.already_existed) return;
          // Optimistically drop the row — server-side KV cache is 60s, so
          // without this the just-tracked row would linger.
          qc.setQueryData<ShortlistResponse>(["shortlist", 10], (prev) => {
            if (!prev) return prev;
            const remaining = prev.jobs.filter((j) => j.url !== job.url);
            return { jobs: remaining, returned: remaining.length };
          });
        },
      },
    );
  }

  return (
    <li className="flex flex-col gap-3 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-neutral-500">
          {job.company_name}
        </p>
        <p className="mt-0.5 truncate text-sm font-medium text-neutral-100">
          {job.title}
        </p>
        {job.location_summary ? (
          <p className="mt-0.5 truncate text-xs text-neutral-500">
            {job.location_summary}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2 sm:shrink-0">
        <ScorePill score={job.score} />
        <a
          href={job.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-neutral-700 bg-neutral-800/40 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-600 hover:bg-neutral-800"
        >
          Open
          <ExternalLink size={12} aria-hidden />
        </a>
        <Button
          variant="secondary"
          size="sm"
          onClick={trackInterested}
          loading={create.isPending}
          leadingIcon={<Plus size={12} aria-hidden />}
        >
          Track
        </Button>
      </div>
    </li>
  );
}

function QuickActionsSidebar() {
  const [trackOpen, setTrackOpen] = useState(false);
  return (
    <aside className="self-start lg:sticky lg:top-4">
      <Card>
        <CardHeader title="Quick actions" />
        <CardBody className="space-y-2">
          <Button
            variant="primary"
            className="w-full justify-start"
            leadingIcon={<Briefcase size={14} aria-hidden />}
            onClick={() => {
              window.location.href = "/jobs";
            }}
          >
            Browse jobs
          </Button>
          <Button
            variant="secondary"
            className="w-full justify-start"
            leadingIcon={<ListChecks size={14} aria-hidden />}
            onClick={() => setTrackOpen(true)}
          >
            Track an application
          </Button>
          <Button
            variant="secondary"
            className="w-full justify-start"
            leadingIcon={<FileText size={14} aria-hidden />}
            onClick={() => {
              window.location.href = "/resumes";
            }}
          >
            Upload a resume
          </Button>
        </CardBody>
      </Card>

      <Dialog open={trackOpen} onOpenChange={setTrackOpen}>
        <TrackForm onDone={() => setTrackOpen(false)} />
      </Dialog>
    </aside>
  );
}

function TrackForm({ onDone }: { onDone: () => void }) {
  const create = useCreateApplication();
  const [url, setUrl] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState<ApplicationStatus>("interested");

  const urlOk = /^https?:\/\//.test(url.trim());
  const canSubmit = urlOk && !create.isPending;
  // When the user pastes a URL with no manual role, the server may run the
  // Browser-Render + AI extract path (5–15s vs the usual sub-second). Show a
  // dedicated affordance so the dialog doesn't look hung.
  const willExtract = create.isPending && !role.trim();

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      { url: url.trim(), role: role.trim() || undefined, status },
      { onSuccess: () => onDone() },
    );
  }

  return (
    <Dialog.Content
      title="Track an application"
      description="Quick add. The Jobs side panel covers the same flow when you find the posting in our catalog."
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onDone}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="track-form"
            variant="primary"
            size="sm"
            loading={create.isPending}
            disabled={!canSubmit}
            leadingIcon={<Plus size={13} aria-hidden />}
          >
            Track
          </Button>
        </>
      }
    >
      <form id="track-form" onSubmit={submit} className="space-y-3">
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
            Posting URL
          </span>
          <Input
            className="mt-1"
            type="url"
            required
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            invalid={url.length > 0 && !urlOk}
          />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
            Role (optional)
          </span>
          <Input
            className="mt-1"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Senior Solutions Engineer"
          />
        </label>
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider text-neutral-500">
            Stage
          </span>
          <Select
            className="mt-1 capitalize"
            value={status}
            onChange={(e) => setStatus(e.target.value as ApplicationStatus)}
          >
            {APPLICATION_STATUSES.map((s) => (
              <option key={s} value={s} className="capitalize">
                {s}
              </option>
            ))}
          </Select>
        </label>
        {willExtract ? (
          <p className="text-[12px] text-neutral-500">
            Extracting role and company from the page… (5–15s for postings outside our catalog)
          </p>
        ) : null}
      </form>
    </Dialog.Content>
  );
}

function formatAge(ms: number): string {
  if (ms < 60_000) return "just now";
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}
